import React, { useState, useEffect, useRef } from "react";
import { TablePagination } from "@mui/material";
import { ArrowBack, CloudUpload } from "@mui/icons-material";
import { useQueryParams } from "../hooks";
import { useDebounce } from "../hooks";
import { useOutletContext, useLocation, useParams, useNavigate } from "react-router-dom";
import {Dialog,DialogTitle,DialogContent,DialogActions,Button} from "@mui/material";
import { getMarketplaceJobsData, getMarketplaceCloseJobsData, closeMarketplaceJob, addResumeInJobs } from "../pages/Dashboard/helper";

export const MarketplaceJobs = () => {
  const [jobsData, setJobsData] = useState([]);
  const { getParam, setMultiParam } = useQueryParams();
  const currentPage = getParam("page");
  const currentLimit = getParam("pageSize");
  const [jobsPage, setJobsPage] = useState(parseInt(currentPage) || 0);
  const [jobsLimit, setJobsLimit] = useState(parseInt(currentLimit) || 10);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState("active");
  const [loadingJobId, setLoadingJobId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [uploadingJobId, setUploadingJobId] = useState(null);
  const debounceSearch = useDebounce(search, 1000);
  const context = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isVendorRoute = location.pathname.includes("Vendor");
  const { userid } = useParams();
  const fileInputRefs = useRef({});

  const handleNavigateBack = () => {
    const baseRoute = isVendorRoute
      ? `/UserManager/Vendor/vendor-info/${userid}`
      : `/UserManager/Client/client-info/${userid}`;
    navigate(`${baseRoute}?page=${getParam("page")}&pageSize=${getParam("pageSize")}&user-name=${getParam("user-name")}`);
  };

  const handleChangePage = (e, newPage) => {
    setJobsPage(newPage);
    fetchJobsData(newPage, jobsLimit);
  };

  const handleChangeRowsPerPage = (event) => {
    setJobsLimit(parseInt(event.target.value, 10));
    setJobsPage(0);
    fetchJobsData(0, parseInt(event.target.value, 10));
  };

  const fetchJobsData = async (page, limit, searchTerm) => {
    try {
      const pageNumber = page + 1;
      const status = activeTab === "active" ? undefined : "CLOSED";
      const response = await getMarketplaceJobsData(pageNumber, limit, searchTerm, userid, status);
      setJobsData(response.data.jobs);
      setJobsTotal(response.data?.total);
    } catch (error) {
      console.error("Error fetching marketplace jobs:", error);
    }
  };
  const handleCloseJob = async (jobId) => {
    try {
      setLoadingJobId(jobId);
      const response = await closeMarketplaceJob(jobId);
      if (response.status === 200 || response.data?.success) {
        fetchJobsData(jobsPage, jobsLimit, debounceSearch);
        console.log("Job closed successfully");
      }
    } catch (error) {
      console.error("Error closing job:", error);
    } finally {
      setLoadingJobId(null);
    }
  };
  const handleUploadClick = (jobId) => {
    if (fileInputRefs.current[jobId]) {
      fileInputRefs.current[jobId].click();
    }
  };

  const handleFileSelect = async (event, jobId, jobName) => {
    const files = Array.from(event.target.files);
    if (!files || files.length === 0) {
      console.log('No file selected');
      return;
    }

    if (files.length > 10) {
      alert('You can select up to 10 resumes at once');
      event.target.value = '';
      return;
    }

    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const maxSizeInBytes = 50 * 1024 * 1024; 
    let totalSize = 0;
    const invalidFiles = [];

    files.forEach((file) => {
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!allowedTypes.includes(fileExtension)) {
        invalidFiles.push(file.name);
      }
      totalSize += file.size;
    });

    if (invalidFiles.length > 0) {
      alert(`Invalid file type(s): ${invalidFiles.join(', ')}. Please select valid resume files (PDF, DOC, DOCX)`);
      event.target.value = '';
      return;
    }
    if (totalSize > maxSizeInBytes) {
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
      alert(`Total file size (${totalSizeMB} MB) exceeds the maximum limit of 50 MB`);
      event.target.value = '';
      return;
    }

    try {
      setUploadingJobId(jobId); 
      const response = await addResumeInJobs(jobId, files, jobName);
      if (response?.data?.error) {
        alert(response.data.error);
        return;
      }
      if (response?.status === 200 || response?.data?.success) {
        const fileCount = files.length;
        alert(`${fileCount} resume${fileCount > 1 ? 's' : ''} uploaded successfully!`);
        fetchJobsData(jobsPage, jobsLimit, debounceSearch);
      } else {
        console.log("Upload failed:", response);
        alert("Failed to upload resume(s). Please try again.");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Please try again.';
      alert(errorMessage);
    } finally {
      setUploadingJobId(null);
      event.target.value = '';
    }
  };

  useEffect(() => {
    const userType = isVendorRoute ? "Vendor" : "Client";
    context(["User Manager", userType, "Marketplace Jobs"]);
    fetchJobsData(jobsPage, jobsLimit, debounceSearch);
  }, [debounceSearch, activeTab]);

  useEffect(() => {
    setMultiParam({ "page": jobsPage, "pageSize": jobsLimit });
  }, [jobsPage, jobsLimit]);

  const handleOpenDialog = (jobId) => {
    setSelectedJobId(jobId);
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJobId(null);
  };
  
  const handleConfirmClose = () => {
    handleCloseJob(selectedJobId);
    handleCloseDialog();
  };
  
  return (
    <div className="flex flex-col">
      <div className="flex py-4 pl-6">
        <button 
          className="pl-2 pr-4 py-2 bg-[#2EBAA3] rounded-md text-[#fff]"
          onClick={handleNavigateBack}
        >
          <ArrowBack sx={{ marginRight: "8px" }} /> Go Back
        </button>
      </div>
      <div className="p-5 shadow-md bg-white">
      <div className="flex flex-wrap justify-between items-center mb-4">
        <h3 className="text-lg uppercase tracking-wider font-bold text-[#2EBAA3]">
          Marketplace Jobs
        </h3>
        <div className="flex gap-4 items-center mt-2 sm:mt-0">
          <div className="flex gap-2">
            <button
              className={`px-4 py-1.5 rounded-md text-sm font-semibold ${
                activeTab === "active" ? "bg-[#2EBAA3] text-white" : "bg-gray-100 text-gray-700"
              }`}
              onClick={() => {
                setActiveTab("active");
                setJobsPage(0);
              }}
            >
              Active
            </button>
            <button
              className={`px-4 py-1.5 rounded-md text-sm font-semibold ${
                activeTab === "closed" ? "bg-[#2EBAA3] text-white" : "bg-gray-100 text-gray-700"
              }`}
              onClick={() => {
                setActiveTab("closed");
                setJobsPage(0);
              }}
            >
              Closed
            </button>
          </div>

          {/* Search */}
          <input
            type="text"
            id="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="px-3 py-1.5 form-control block text-base font-normal text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out focus:text-gray-700 focus:bg-white focus:outline-none"
          />
        </div>
        </div>
        <br />
        <div className="overflow-x-auto relative">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Job Name</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Posted Date</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Salary</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Action</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Number of position</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap text-center">
                  <div>All</div>
                  <div>Applicants</div>
                </th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">
                  <div>Screened</div>
                </th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">
                  <div>Reviewed</div>
                </th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">
                  <div>Interview</div>
                  <div>Scheduled</div>
                </th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Job Offered</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Hired</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap border-r-2 border-gray-300">Rejected</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Recommended</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Good Fit</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Average</th>
                <th scope="col" className="py-3 px-6 whitespace-nowrap">Below Average</th>
                {activeTab === "active" && (<th scope="col" className="py-3 px-6 whitespace-nowrap">Close job</th>)}
              </tr>
            </thead>
            <tbody>
              {jobsData?.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === "active" ? "17" : "16"} className="py-4 px-6 text-center text-gray-500">No Data Found</td>
                </tr>
              ) : (
                jobsData.map((job) => (
                  <tr key={job.id} className="bg-white border-b">
                    <td className="py-4 px-6 whitespace-nowrap">{job.name}</td>
                    <td className="py-4 px-6 whitespace-nowrap">
                      {job.job_posted_date ? new Date(job.job_posted_date).toISOString().split("T")[0] : "N/A"}
                    </td>
                    <td className="py-4 px-6">
                      {job.salary.currency === "INR" ? "₹" : "$"}
                      {job.salary.min}{job.salary.max ? ` - ${job.salary.max}` : ""}{" "}
                      /{job.salary.type}
                    </td>
                    <td className="py-4 px-3 whitespace-nowrap">
                      <button
                        onClick={() => handleUploadClick(job.id)}
                        disabled={uploadingJobId === job.id}
                        className="flex items-center px-2 py-1.5 bg-[#2EBAA3] text-white rounded-md text-xs font-semibold hover:bg-[#259b87] transition-colors disabled:opacity-50"
                      >
                        <CloudUpload sx={{ fontSize: 14, marginRight: "2px" }} />
                        {uploadingJobId === job.id ? "Uploading..." : "Add Candidate"}
                      </button>
                      <input
                        type="file"
                        ref={el => fileInputRefs.current[job.id] = el}
                        onChange={(e) => handleFileSelect(e, job.id, job.name)}
                        accept=".pdf,.doc,.docx"
                        multiple
                        style={{ display: 'none' }}
                      />
                    </td>
                    <td className="py-4 px-16">{job.number_of_position || 0}</td>
                    <td className="py-4 px-6">{job.applicant_count}</td>
                    <td className="py-4 px-6">{job.applicationCounts?.premium_screening || 0}</td>
                    <td className="py-4 px-6">{job.applicationCounts?.screening || 0}</td>
                    <td className="py-4 px-6">{job.applicationCounts?.interview_scheduled || 0}</td>
                    <td className="py-4 px-6">{job.applicationCounts?.job_offered || 0}</td>
                    <td className="py-4 px-6">{job.applicationCounts?.hired || 0}</td>
                    <td className="py-4 px-6 border-r-2 border-gray-300">{job.applicationCounts?.reject || 0}</td>
                    <td className="py-4 px-6">{job?.applicationStrengthCounts?.Recommended || 0}</td>
                    <td className="py-4 px-6">{job?.applicationStrengthCounts?.["Good Fit"] || 0}</td>
                    <td className="py-4 px-6">{job?.applicationStrengthCounts?.Average || 0}</td>
                    <td className="py-4 px-6">{job?.applicationStrengthCounts?.["Below Average"] || 0}</td>
                    {activeTab === "active" &&<td className="py-4 px-6">
                    <button onClick={() => handleOpenDialog(job.id)} disabled={loadingJobId === job.id} className="px-4 py-1.5 w-16 rounded-md text-sm font-semibold bg-[#2EBAA3] text-white">
                      Close
                    </button>
                    </td>}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          component="div"
          count={jobsTotal}
          page={jobsPage}
          onPageChange={handleChangePage}
          rowsPerPage={jobsLimit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
        />
      </div>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Close Job</DialogTitle>
        <DialogContent>Are you sure you want to close this job?</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmClose}
            color="primary"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};